import React, { useState, useEffect } from 'react'
import {
    Stack,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    MenuItem,
    Divider,
    Snackbar,
    Alert,
    Box,
    Grid,
    IconButton,
    Autocomplete
} from '@mui/material'
import { HiPlus, HiTrash } from 'react-icons/hi'
import { getActiveEmployees } from '../../services/EmployeeService'
import { createAssignItem, getActiveCategories, getItemAvailable } from '../../services/InventoryService'

const ItemAssignment = () => {
    const [employees, setEmployees] = useState([])
    const [categories, setCategories] = useState([])

    const [rows, setRows] = useState([{ category: '', itemId: '', items: [], stock: 0, qty: 1 }])

    const [form, setForm] = useState({
        employeeId: '',
        assignmentDate: new Date().toISOString().slice(0, 10),
        location: '',
        remarks: ''
    })

    const [loading, setLoading] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    useEffect(() => {
        loadEmployees()
        loadCategories()
    }, [])

    const loadEmployees = async () => {
        const res = await getActiveEmployees()
        setEmployees(res || [])
    }

    const loadCategories = async () => {
        const res = await getActiveCategories()
        setCategories(res || [])
    }

    const loadItems = async (index, category) => {
        try {
            const result = await getItemAvailable({ category })
            updateRow(index, 'items', result || [])
            updateRow(index, 'itemId', '')
            updateRow(index, 'stock', 0)
        } catch (err) {
            console.error(err)
        }
    }

    const updateRow = (index, field, value) => {
        const updated = [...rows]
        updated[index][field] = value

        if (field === 'itemId') {
            const item = updated[index].items.find((i) => i.id === value)
            updated[index].stock = item?.quantity || 0
        }

        setRows(updated)
    }

    const addRow = () => {
        setRows([...rows, { category: '', itemId: '', items: [], stock: 0, qty: 1 }])
    }

    const removeRow = (index) => {
        setRows(rows.filter((_, i) => i !== index))
    }

    const handleSubmit = async () => {
        if (!form.employeeId && !form.location) {
            return setSnackbar({
                open: true,
                message: 'Employee or location required',
                severity: 'error'
            })
        }

        if (rows.length === 0) {
            return setSnackbar({
                open: true,
                message: 'Add at least one item',
                severity: 'error'
            })
        }

        for (const row of rows) {
            if (!row.itemId) {
                return setSnackbar({
                    open: true,
                    message: 'Complete all item rows',
                    severity: 'error'
                })
            }
        }

        try {
            setLoading(true)
            const payload = {
                employeeId: form.employeeId,
                assignDate: form.assignmentDate,
                location: form.location,
                remarks: form.remarks,
                items: rows.map((row) => row.itemId) // ✅ simple array
            }

            const response = await createAssignItem(payload)
            console.log('API Response:', response)
            if (response.error) {
                throw new Error(response.error)
            } else {
                setSnackbar({
                    open: true,
                    message: 'Items assigned successfully',
                    severity: 'success'
                })
                // reset
                setRows([{ category: '', itemId: '', items: [] }])
                setForm({
                    employeeId: '',
                    assignmentDate: new Date().toISOString().slice(0, 10),
                    location: '',
                    remarks: ''
                })
            }
        } catch (err) {
            console.error(err)

            let errorMessage = 'Something went wrong'

            if (err.response?.data?.message) {
                // if message is array → join
                if (Array.isArray(err.response.data.message)) {
                    errorMessage = err.response.data.message.join(', ')
                } else {
                    errorMessage = err.response.data.message
                }
            } else if (err.message) {
                errorMessage = err.message
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Stack p={3} alignItems="center" spacing={2}>
            <Typography variant="h5" fontWeight="bold">
                Item Assignment
            </Typography>

            <Card sx={{ maxWidth: 900, width: '100%', borderRadius: 3 }}>
                <CardContent>
                    <Stack spacing={3}>
                        {/* Employee */}
                        {/* <TextField
                            select
                            fullWidth
                            label="Employee"
                            value={form.employeeId}
                            onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                        >
                            {employees.map((e) => (
                                <MenuItem key={e.employeeId} value={e.employeeId}>
                                    {e.firstName} {e.lastName}
                                </MenuItem>
                            ))}
                        </TextField> */}

                        <Autocomplete
                            fullWidth
                            options={employees}
                            value={employees.find((emp) => emp.employeeId === form.employeeId) || null}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                            onChange={(_, value) => {
                                setForm({
                                    ...form,
                                    employeeId: value?.employeeId || ''
                                })
                            }}
                            renderInput={(params) => <TextField {...params} label="Employee" />}
                        />

                        <Divider />

                        {/* Assigned Items */}
                        <Typography fontWeight="bold">Assigned Items</Typography>

                        {rows.map((row, index) => (
                            <Box
                                key={index}
                                sx={{
                                    border: '1px solid #eee',
                                    borderRadius: 2,
                                    p: 2
                                }}
                            >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                                    {/* Category */}
                                    <TextField
                                        fullWidth
                                        select
                                        label="Category"
                                        value={row.category}
                                        onChange={(e) => {
                                            updateRow(index, 'category', e.target.value)
                                            loadItems(index, e.target.value)
                                        }}
                                    >
                                        {categories.map((c) => (
                                            <MenuItem key={c.id} value={c.name}>
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {/* Item */}
                                    {/* <TextField
                                        fullWidth
                                        select
                                        label="Item / Serial"
                                        value={row.itemId}
                                        onChange={(e) => updateRow(index, 'itemId', e.target.value)}
                                    >
                                        {row.items.map((i) => (
                                            <MenuItem key={i.id} value={i.id}>
                                                {i.brand} - {i.model} - {i.serialNumber}
                                            </MenuItem>
                                        ))}
                                    </TextField> */}

                                    <Autocomplete
                                        fullWidth
                                        options={row.items}
                                        getOptionLabel={(option) =>
                                            `${option.brand} - ${option.model} - ${option.serialNumber}`
                                        }
                                        onChange={(_, value) => {
                                            updateRow(index, 'itemId', value?.id || '')
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Item / Serial" />}
                                    />

                                    <Button
                                        color="error"
                                        variant="outlined"
                                        onClick={() => removeRow(index)}
                                        sx={{
                                            minWidth: '56px',
                                            height: '56px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <HiTrash size={20} />
                                    </Button>
                                </Stack>
                            </Box>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<HiPlus />}
                            onClick={addRow}
                            sx={{ alignSelf: 'flex-end' }}
                        >
                            Add Item
                        </Button>

                        <Divider />

                        {/* Assignment Details */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Assignment Date"
                                    value={form.assignmentDate}
                                    onChange={(e) => setForm({ ...form, assignmentDate: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Location"
                                    value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Remarks"
                                    value={form.remarks}
                                    onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Assigning...' : 'Assign Items'}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Stack>
    )
}

export default ItemAssignment
